// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminRolesFuncionales (prompt 0473 M2)
// Tabla admin de Roles Funcionales homogeneizada con AdminRolesGobernanza:
//  - Orden canónico: Código | Nombre | Descripción | Nivel min — max | Puestos | Activo | Ficha | Acciones
//  - 3 acciones idénticas: editar · toggle activo · borrar
//  - Columna Ficha con botón Crear FR / Ver FR (reemplaza check mudo)
//  - Toggle "Mostrar inactivos" + contador inferior (compartido con RG)
//  - Placeholder unificado a un solo em-dash `—`
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  fetchRolesFuncionales,
  fetchNivelesPuesto,
  actualizarRolFuncional,
  desactivarRolFuncional,
  type RolFuncional,
  type NivelPuesto,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { AdminRolFuncionalPanel } from './rol-funcional-panel/AdminRolFuncionalPanel';
import type { RolFuncionalTabId } from './rol-funcional-panel/AdminRolFuncionalPanel';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

interface PanelState {
  rol: RolFuncional | null; // null = nuevo
  tab: RolFuncionalTabId;
}

// ─── Icons ───────────────────────────────────────────────────

function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M8.5 1.5L10.5 3.5L4 10L1.5 10.5L2 8L8.5 1.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2 3H10M4.5 3V2C4.5 1.5 4.8 1 5.5 1H6.5C7.2 1 7.5 1.5 7.5 2V3M3 3L3.5 10.5C3.5 10.8 3.8 11 4 11H8C8.2 11 8.5 10.8 8.5 10.5L9 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PowerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4 2.5C2.8 3.2 2 4.5 2 6c0 2.2 1.8 4 4 4s4-1.8 4-4c0-1.5-.8-2.8-2-3.5M6 1.5V6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────

export function AdminRolesFuncionales() {
  const { t } = useI18n();
  const adm = ((t.structure as Record<string, unknown>)?.admin ?? {}) as Record<string, string>;
  const tooltips = (adm.tooltips as unknown as Record<string, string> | undefined) ?? {};

  const [roles, setRoles] = useState<RolFuncional[]>([]);
  const [niveles, setNiveles] = useState<NivelPuesto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [panelState, setPanelState] = useState<PanelState | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, nRes] = await Promise.all([fetchRolesFuncionales(), fetchNivelesPuesto()]);
      setRoles(rRes.data ?? []);
      setNiveles(nRes.data ?? []);
    } catch {
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const nivelLabel = (id: string | null) => {
    if (!id) return null;
    const n = niveles.find((nv) => nv.id === id);
    return n ? (n.nombreCorto ?? n.nombre) : null;
  };

  const handleToggleActivo = async (rol: RolFuncional) => {
    try {
      await actualizarRolFuncional(rol.id, { activo: !rol.activo });
      load();
      showToast(rol.activo ? 'Rol desactivado' : 'Rol activado');
    } catch {
      showToast('Error al cambiar estado');
    }
  };

  const handleDeactivateOrDelete = async (rol: RolFuncional) => {
    const puestosCount = rol._count?.puestos ?? 0;
    const msg =
      puestosCount > 0
        ? (adm.confirmDeactivateRol ??
          `Este rol tiene ${puestosCount} puesto(s) asociado(s). Se desactivara en vez de eliminar. Continuar?`)
        : 'Eliminar este rol funcional?';
    if (!window.confirm(msg)) return;
    try {
      if (puestosCount > 0) {
        await actualizarRolFuncional(rol.id, { activo: false });
        showToast('Rol desactivado');
      } else {
        await desactivarRolFuncional(rol.id);
        showToast('Rol eliminado');
      }
      load();
    } catch {
      showToast('Error al procesar');
    }
  };

  // Filtros: activo (toggle mostrarInactivos) + busqueda
  const q = search.toLowerCase().trim();
  const filtered = useMemo(() => {
    const base = mostrarInactivos ? roles : roles.filter((r) => r.activo);
    if (!q) return base;
    return base.filter(
      (r) =>
        r.nombre.toLowerCase().includes(q) ||
        (r.descripcion ?? '').toLowerCase().includes(q) ||
        (r.codigo ?? '').toLowerCase().includes(q),
    );
  }, [roles, mostrarInactivos, q]);

  // ─── Columnas canonicas (orden homogéneo con RG) ────────────
  const columns: AdminTableColumn<RolFuncional>[] = [
    {
      key: 'codigo',
      label: adm.colCodigo ?? 'Código',
      mono: true,
      sortable: true,
      sortKey: (r) => r.codigo ?? '',
      width: 110,
      render: (r) => r.codigo ?? '—',
    },
    {
      key: 'nombre',
      label: adm.colNombre ?? 'Nombre',
      sortable: true,
      sortKey: (r) => r.nombre,
      render: (r) => <span style={{ fontWeight: 600 }}>{r.nombre}</span>,
    },
    {
      key: 'descripcion',
      label: adm.descripcion ?? 'Descripción',
      render: (r): ReactNode => (
        <span
          style={{
            color: '#6B7280',
            display: 'inline-block',
            maxWidth: 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
          title={r.descripcion ?? ''}
        >
          {r.descripcion ?? '—'}
        </span>
      ),
    },
    {
      key: 'nivel',
      label: `${adm.nivelMinimo ?? 'Nivel min.'} — ${adm.nivelMaximo ?? 'Nivel max.'}`,
      render: (r) => {
        const min = nivelLabel(r.nivelMinimo);
        const max = nivelLabel(r.nivelMaximo);
        if (!min && !max) return <span style={{ color: '#CBD5E1' }}>—</span>;
        return (
          <span>
            {min ?? '—'} — {max ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'puestos',
      label: adm.puestosAsociados ?? 'Puestos',
      align: 'right',
      sortable: true,
      mono: true,
      sortKey: (r) => r._count?.puestos ?? 0,
      render: (r) => r._count?.puestos ?? 0,
    },
    {
      key: 'activo',
      label: adm.activo ?? 'Activo',
      align: 'center',
      render: (r) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: r.activo ? '#D1FAE5' : '#FEE2E2',
            color: r.activo ? '#065F46' : '#991B1B',
            whiteSpace: 'nowrap',
          }}
        >
          {r.activo ? (adm.activo ?? 'Activo') : (adm.inactivo ?? 'Inactivo')}
        </span>
      ),
    },
    {
      key: 'ficha',
      label: adm.fichaFR ?? 'Ficha FR',
      align: 'center',
      render: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPanelState({ rol: r, tab: 'ficha' });
          }}
          style={{
            background: 'none',
            border: '1px solid #D1D5DB',
            padding: '3px 10px',
            borderRadius: 6,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: r.fichaDocumentoId ? '#1F3864' : '#6B7280',
          }}
        >
          {r.fichaDocumentoId ? (adm.fichaVer ?? 'Ver FR') : (adm.fichaCrear ?? 'Crear FR')}
        </button>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={adm.rolesFuncionales ?? 'Roles funcionales'}
        infoTooltip={{
          title: 'Roles funcionales',
          text:
            'Catálogo de roles funcionales de la organización.\n\n' +
            'Un rol funcional agrupa responsabilidades homogéneas (ej: Responsable de Calidad, Analista Financiero) ' +
            'y define el rango de niveles jerárquicos en los que puede ejercerse.\n\n' +
            'Cada rol tiene un código único tenant-wide (RF-NNN) y puede asociarse a múltiples puestos. ' +
            'La Ficha FR documenta competencias, certificaciones y condiciones del rol.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search ?? 'Buscar...',
        }}
        extras={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            {adm.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
        onNew={{
          label: adm.newRolFuncional ?? 'Nuevo rol',
          onClick: () => setPanelState({ rol: null, tab: 'datos' }),
        }}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        onRowClick={(r) => setPanelState({ rol: r, tab: 'datos' })}
        loading={loading}
        loadingMessage={adm.cargando ?? 'Cargando…'}
        emptyMessage={adm.noResults ?? 'Sin resultados'}
        actions={(r) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label={tooltips.editar ?? 'Editar'}
              onClick={() => setPanelState({ rol: r, tab: 'datos' })}
            />
            <AdminActionButton
              icon={<PowerIcon />}
              label={
                r.activo
                  ? (tooltips.toggleDesactivar ?? 'Desactivar')
                  : (tooltips.toggleActivar ?? 'Activar')
              }
              onClick={() => handleToggleActivo(r)}
            />
            <AdminActionButton
              icon={<TrashIcon />}
              label={tooltips.eliminar ?? 'Eliminar'}
              variant="danger"
              onClick={() => handleDeactivateOrDelete(r)}
            />
          </>
        )}
        footer={
          !loading ? (
            <span>
              {filtered.length} {adm.de ?? 'de'} {roles.length} {adm.rolesContador ?? 'roles'}
            </span>
          ) : undefined
        }
      />

      {panelState && (
        <AdminRolFuncionalPanel
          rol={panelState.rol}
          niveles={niveles}
          initialTab={panelState.tab}
          onClose={() => setPanelState(null)}
          onSaved={() => {
            load();
            showToast(adm.save ?? 'Guardado');
          }}
        />
      )}
    </div>
  );
}
